<script setup lang="ts">
import AppLayout from '../../layouts/AppLayout.vue'
</script>

<template>
  <AppLayout>
    <div class="space-y-6">
      <!-- Header -->
      <div>
        <h1 class="text-3xl font-bold text-cc-text tracking-tight">Documentation</h1>
        <p class="text-cc-text-secondary mt-1">
          Guides, API references, and resources to help you get the most out of Channel Standard Companion.
        </p>
      </div>

      <!-- Coming Soon Banner -->
      <div class="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-100 dark:border-blue-800 rounded-xl p-6">
        <div class="flex items-start gap-4">
          <div class="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <div>
            <h3 class="font-bold text-blue-900 dark:text-blue-100 text-lg">Documentation Coming Soon</h3>
            <p class="text-blue-700 dark:text-blue-300 mt-1">
              We're working hard to build comprehensive documentation. Check back soon for guides, tutorials, and API references.
            </p>
          </div>
        </div>
      </div>

      <!-- Documentation Categories Preview -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <!-- Getting Started -->
        <div class="bg-cc-elevated rounded-xl border border-cc-border p-6 opacity-75">
          <div class="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-4">
            <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 class="font-bold text-cc-text mb-2">Getting Started</h3>
          <p class="text-sm text-cc-text-secondary mb-4">
            Quick start guides to help you navigate the platform and set up your account.
          </p>
          <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-cc-hover text-cc-text-secondary">
            Coming Soon
          </span>
        </div>

        <!-- Advisor Guides -->
        <div class="bg-cc-elevated rounded-xl border border-cc-border p-6 opacity-75">
          <div class="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center mb-4">
            <svg class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 class="font-bold text-cc-text mb-2">Advisor Guides</h3>
          <p class="text-sm text-cc-text-secondary mb-4">
            Learn how to manage deals, use AI recommendations, and track supplier relationships.
          </p>
          <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-cc-hover text-cc-text-secondary">
            Coming Soon
          </span>
        </div>

        <!-- Supplier Guides -->
        <div class="bg-cc-elevated rounded-xl border border-cc-border p-6 opacity-75">
          <div class="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mb-4">
            <svg class="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 class="font-bold text-cc-text mb-2">Supplier Guides</h3>
          <p class="text-sm text-cc-text-secondary mb-4">
            Manage your company profile, products, promotions, and training materials.
          </p>
          <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-cc-hover text-cc-text-secondary">
            Coming Soon
          </span>
        </div>

        <!-- API Reference -->
        <div class="bg-cc-elevated rounded-xl border border-cc-border p-6 opacity-75">
          <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4">
            <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          </div>
          <h3 class="font-bold text-cc-text mb-2">API Reference</h3>
          <p class="text-sm text-cc-text-secondary mb-4">
            Technical documentation for integrating with the Channel Standard API.
          </p>
          <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-cc-hover text-cc-text-secondary">
            Coming Soon
          </span>
        </div>

        <!-- Best Practices -->
        <div class="bg-cc-elevated rounded-xl border border-cc-border p-6 opacity-75">
          <div class="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center mb-4">
            <svg class="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <h3 class="font-bold text-cc-text mb-2">Best Practices</h3>
          <p class="text-sm text-cc-text-secondary mb-4">
            Tips and strategies for maximizing your success on the platform.
          </p>
          <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-cc-hover text-cc-text-secondary">
            Coming Soon
          </span>
        </div>

        <!-- Video Tutorials -->
        <div class="bg-cc-elevated rounded-xl border border-cc-border p-6 opacity-75">
          <div class="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center mb-4">
            <svg class="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 class="font-bold text-cc-text mb-2">Video Tutorials</h3>
          <p class="text-sm text-cc-text-secondary mb-4">
            Step-by-step video walkthroughs of key platform features.
          </p>
          <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-cc-hover text-cc-text-secondary">
            Coming Soon
          </span>
        </div>
      </div>

      <!-- Newsletter Signup Placeholder -->
      <div class="bg-cc-elevated rounded-xl border border-cc-border p-6">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 class="font-bold text-cc-text">Stay Updated</h3>
            <p class="text-sm text-cc-text-secondary mt-1">
              We'll notify you when new documentation is available.
            </p>
          </div>
          <button disabled class="px-6 py-2.5 bg-cc-hover text-gray-400 dark:text-gray-500 rounded-lg font-medium cursor-not-allowed">
            Notifications Coming Soon
          </button>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
